
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProtectedRoute from '@/components/ProtectedRoute';
import Header from '@/components/Header';
import KanbanBoard from '@/components/kanban/KanbanBoard';
import MobileKanbanBoard from '@/components/mobile/MobileKanbanBoard';
import { LoadingErrorBoundary } from '@/components/LoadingErrorBoundary';
import { useBoardContext } from '@/contexts/BoardContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Navigate } from 'react-router-dom';
import { CardDialog } from '@/components/kanban/CardDialog';
import { CalendarCardDialog } from '@/components/CalendarCardDialog';

const Index = () => {
  const { 
    currentBoardId, 
    boardsLoading, 
    boardsError, 
    refetchBoards,
    allCards,
    canEditCards
  } = useBoardContext();
  
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const cardId = searchParams.get('card');
  const [selectedCard, setSelectedCard] = useState<any>(null);

  // Find the card when cardId is present in URL
  useEffect(() => {
    if (cardId && allCards) {
      const card = allCards.find(c => c.id === cardId);
      setSelectedCard(card || null);
    } else {
      setSelectedCard(null);
    }
  }, [cardId, allCards]);

  const handleCardDialogClose = () => {
    setSelectedCard(null);
    // Remove card parameter from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('card');
    setSearchParams(newSearchParams);
  };

  if (!currentBoardId) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ThemeProvider>
      <ProtectedRoute>
        <div className="h-screen w-full bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex flex-col">
          {!isMobile && <Header />}
          <div className="flex-1 overflow-hidden">
            <LoadingErrorBoundary 
              isLoading={boardsLoading} 
              error={boardsError}
              onRetry={refetchBoards}
              loadingMessage="Loading your boards..."
              connectionState="connected"
            >
              {isMobile ? (
                <MobileKanbanBoard key={currentBoardId} />
              ) : (
                <KanbanBoard key={currentBoardId} />
              )}
            </LoadingErrorBoundary>
          </div>

          {/* Card Dialog - opens when navigating from dashboard */}
          {selectedCard && (
            <>
              {canEditCards ? (
                <CardDialog
                  card={selectedCard}
                  open={!!selectedCard}
                  onOpenChange={(open) => {
                    if (!open) handleCardDialogClose();
                  }}
                  isCreateMode={false}
                />
              ) : (
                <CalendarCardDialog
                  card={selectedCard}
                  open={!!selectedCard}
                  onOpenChange={(open) => {
                    if (!open) handleCardDialogClose();
                  }}
                />
              )}
            </>
          )}
        </div>
      </ProtectedRoute>
    </ThemeProvider>
  );
};

export default Index;
